/**
 * Navigation reference — the single source of truth mapping every menu entry to
 * its page id, slug, and page name (Policy 8 of create-frontend-layout-components).
 *
 * The layout's navigation is driven from this file. Menu items route by **slug**
 * (never by page id); the app's routes are keyed on these same slugs so nav and
 * routing stay in sync. Groups (`children`) render as dropdowns; a group has no
 * slug of its own. Icons are FontAwesome classes carried over from the design's
 * `menuData`.
 *
 * When ui-components-from-reference builds a page, it wires the page's route to
 * the matching `slug` here. Until a page component exists, its slug has no route
 * and the router catch-all renders the themed 404 — so the nav never dead-ends.
 */

// Flat list of every navigable page (leaf menu items) — id ↔ slug ↔ name.
export const pages = [
  { pageId: 'a250adba-6715-4ed5-8baa-3bb7708edb12', slug: 'home', name: 'Home', label: 'Home', icon: 'fa fa-home', isDefault: true },
  { pageId: '2b70a7e2-82cb-4983-8850-1476d9f654f6', slug: 'site-registry', name: 'Site Registry and Asset Onboarding', label: 'Site Registry', icon: 'fa fa-building' },
  { pageId: 'a20c1137-a9bb-4375-9136-67981c1cbf11', slug: 'telemetry-go-live', name: 'Telemetry Go-Live Wizard', label: 'Telemetry Go-Live', icon: 'fa fa-plug' },
  { pageId: '19df9f3d-124f-4c95-ab1d-683803a08769', slug: 'workflow-tracker', name: 'Net-Metering Workflow Tracker', label: 'Workflow Tracker', icon: 'fa fa-tasks' },
  { pageId: 'dd1a76ce-7e73-4fda-8ebb-0412eea49b0a', slug: 'evidence-assembler', name: 'Deemed-Approval Evidence Assembler', label: 'Evidence Assembler', icon: 'fa fa-file-archive' },
  { pageId: '9e4295af-caa7-4edf-8ac5-ce3dd563f466', slug: 'graphiq-brain', name: 'GraphIQ Compliance Workspace', label: 'GraphIQ Brain', icon: 'fa fa-brain' },
  { pageId: '623b9641-0604-4a60-9588-4e188ce78df3', slug: 'rec-ledger', name: 'REC Generation and Lineage Ledger', label: 'REC Ledger', icon: 'fa fa-receipt' },
  { pageId: '0dcf72cd-b74b-4a48-8ec8-45b6517613d7', slug: 'prosumer-pooling', name: 'Prosumer REC Aggregation Portal', label: 'Prosumer Pooling', icon: 'fa fa-users' },
  { pageId: '9c5a8f5e-a122-40bf-8a33-911002199351', slug: 'utility-rps', name: 'Utility RPS Compliance Console', label: 'Utility RPS', icon: 'fa fa-shield-alt' },
  { pageId: '0057ca21-24cb-4d4a-a329-ea095d0425e3', slug: 'mrv-emissions', name: 'MRV Reporting and Emissions Inventory', label: 'MRV & Emissions', icon: 'fa fa-chart-bar' },
  { pageId: 'b53a1516-0ac1-4abf-8318-8a2224ab4d9d', slug: 'verifier-space', name: 'Verifier Verification Workspace', label: 'Verifier Space', icon: 'fa fa-user-check' },
  { pageId: 'ed1f40ec-9aef-46e7-802e-c26844316bb4', slug: 'monetization', name: 'Milestone and Annuity Billing Dashboard', label: 'Monetization', icon: 'fa fa-file-invoice-dollar' },
  { pageId: '1478874d-167c-4afb-b065-cf4601dc71a3', slug: 'administration', name: 'Administration Dashboard', label: 'Dashboard', icon: 'fa fa-gauge-high' },
  { pageId: 'user-management', slug: 'user-management', name: 'User Management', label: 'User Management', icon: 'fa fa-users-cog' },
];

const byLabel = (label) => pages.find((p) => p.label === label);

// The navigation tree the layout renders — groups (dropdowns) + leaf links.
// Mirrors the design reference's `menuData.Children` structure and order.
export const menuData = [
  byLabel('Home'),
  {
    label: 'Acquisition',
    icon: 'fa fa-arrow-circle-down',
    children: [byLabel('Site Registry'), byLabel('Telemetry Go-Live'), byLabel('Workflow Tracker'), byLabel('Evidence Assembler')],
  },
  {
    label: 'Compliance & RECs',
    icon: 'fa fa-certificate',
    children: [byLabel('GraphIQ Brain'), byLabel('REC Ledger'), byLabel('Prosumer Pooling'), byLabel('Utility RPS')],
  },
  {
    label: 'Emissions & ESG',
    icon: 'fa fa-leaf',
    children: [byLabel('MRV & Emissions'), byLabel('Verifier Space')],
  },
  byLabel('Monetization'),
  {
    label: 'Administration',
    icon: 'fa fa-cogs',
    children: [byLabel('Dashboard'), byLabel('User Management')],
  },
];

// The app's landing page (design `is_default_page` / menuData.DefaultMenuItem).
export const defaultPage = byLabel('Home');

export default menuData;
